import { FC, useState, useMemo, memo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store";
import { PairSelect } from "features/select-pair";
import { SelectDrawingTool } from "features/drawing-tool";
import { SelectedIndicators } from "features/indicators/ui/select-indicators/SelectIndicators";
import { formatPriceForAnimation } from "utils/priceFormatter";
import type { ChartType as TChartType } from "shared/model";
import { SettingsIcon as SettingsIconTSX } from "shared/ui";
import { ChartWithIndicators } from "./Chart";
import { Spiner } from "features/chart";
import ChartTimeframe from "./ChartTimeframe";
import ChartType from "./ChartType";
import Modal from "./Modal";
import { ClearAllButton } from "./ClearAllButton";
import ChartSettings from "./ChartSettings";
import SettingsIcon from "../assets/svg/settings-active.svg";
import doubleArrowIcon from "../assets/svg/double-arrow.svg";

import "./PriceChart.css";

type Props = {
  tradingPanelVisible: boolean;
  onOpenTradingPanel: () => void;
};

const PriceChart: FC<Props> = ({ tradingPanelVisible, onOpenTradingPanel }) => {
  const { selectedPair, prices } = useSelector(
    (state: RootState) => state.trading,
  );
  const { isConnected, connectionStatus } = useSelector(
    (state: RootState) => state.websocket,
  );

  // Track whether we've had at least one successful connection this session.
  // Used to distinguish "initial connecting" (show spinner) from "lost connection" (show error).
  const hasEverConnected = useRef(false);
  useEffect(() => {
    if (isConnected) hasEverConnected.current = true;
  }, [isConnected]);

  // Stay in spinner until the chart has rendered its first batch of data.
  const [isChartReady, setIsChartReady] = useState(false);

  // Состояние для типа графика
  const [chartType, setChartType] = useState<TChartType>("candlestick");

  const getCurrentPrice = () => {
    if (!selectedPair || !prices[selectedPair]) return null;
    return prices[selectedPair];
  };

  const currentPrice = useMemo(() => getCurrentPrice(), [selectedPair, prices]);

  const [isChartSettingsModalOpen, setIsChartSettingsModalOpen] =
    useState(false);

  const showNoConnection =
    !isConnected &&
    (connectionStatus === "error" ||
      (connectionStatus === "disconnected" && hasEverConnected.current));

  const showConnecting = !isConnected && !showNoConnection;

  return (
    <div className="chart">
      {showNoConnection ? (
        <div className="chart-message">
          <span>🔴 No connection to server</span>
          <p>Connect to the server to get data</p>
        </div>
      ) : showConnecting ? (
        <Spiner />
      ) : (
        <>
          {!isChartReady && (
            <div className="chart-loading-overlay">
              <Spiner />
            </div>
          )}

          <div className="chart__header">
            <div className="chart__header-left">
              <PairSelect disabled={!isConnected} />
              <SelectDrawingTool />
              <SelectedIndicators />
              <ClearAllButton />

              {selectedPair && currentPrice && (
                <div className="current-price">
                  <span className="current-price__label">{selectedPair}:</span>
                  <span className="current-price__value">
                    {formatPriceForAnimation(currentPrice.price, selectedPair)}
                  </span>
                </div>
              )}
            </div>

            {selectedPair && (
              <>
                <div className="chart__controls">
                  <ChartTimeframe />
                  <ChartType
                    chartType={chartType}
                    setChartType={setChartType}
                  />
                </div>

                <button
                  className="button chart-controls-button"
                  onClick={() => {
                    setIsChartSettingsModalOpen(true);
                  }}
                >
                  <SettingsIconTSX />
                </button>

                <Modal
                  isOpen={isChartSettingsModalOpen}
                  onClose={() => {
                    setIsChartSettingsModalOpen(false);
                  }}
                  icon={SettingsIcon}
                  title="Chart Settings"
                >
                  <ChartSettings
                    chartType={chartType}
                    setChartType={setChartType}
                  />
                </Modal>

                {!tradingPanelVisible && (
                  <button
                    className="button trading-panel__toggle-button trading-panel__toggle-button--open"
                    onClick={onOpenTradingPanel}
                    title="Show Trading Panel"
                  >
                    <img src={doubleArrowIcon} alt="Show" />
                  </button>
                )}
              </>
            )}
          </div>

          <div className="chart-area">
            {selectedPair && (
              <ChartWithIndicators
                symbol={selectedPair}
                chartType={chartType}
                onReady={() => setIsChartReady(true)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(PriceChart);
